import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { isDefined, isNonEmptyArray } from 'app-shared/utils';
import { IconSearch } from 'app-ui/icon';
import { ListItem } from 'app-ui/primitives/navigation';
import { themeCssVariables, useTheme } from 'app-ui/theme';
import { type ChangeEvent, type KeyboardEvent, useId, useState } from 'react';

import { COMMAND_MENU_GROUPS } from '@/command-menu/constants/CommandMenuGroups';
import { useCloseCommandMenu } from '@/command-menu/hooks/useCloseCommandMenu';
import { useCommandMenuCommands } from '@/command-menu/hooks/useCommandMenuCommands';
import { type CommandMenuCommand } from '@/command-menu/types/CommandMenuCommand';
import { filterCommandMenuCommands } from '@/command-menu/utils/filterCommandMenuCommands';
import { HotkeyHint } from '@/ui/utilities/hotkey/components/HotkeyHint';

const StyledSearchRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${themeCssVariables.border.color.medium};
  color: ${themeCssVariables.font.color.tertiary};
  display: flex;
  flex: none;
  gap: ${themeCssVariables.spacing[2]};
  height: ${themeCssVariables.spacing[12]};
  padding: 0 ${themeCssVariables.spacing[4]};
`;

const StyledSearchInput = styled.input`
  background: transparent;
  border: none;
  color: ${themeCssVariables.font.color.primary};
  flex: 1;
  font-family: ${themeCssVariables.font.family};
  font-size: ${themeCssVariables.font.size.md};
  height: 100%;
  min-width: 0;
  outline: none;
  padding: 0;

  &::placeholder {
    color: ${themeCssVariables.font.color.light};
    font-weight: ${themeCssVariables.font.weight.medium};
  }
`;

const StyledList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${themeCssVariables.spacing[2]};
  min-height: 0;
  overflow-y: auto;
  padding: ${themeCssVariables.spacing[2]};
`;

const StyledGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeCssVariables.betweenSiblingsGap};
`;

const StyledGroupLabel = styled.div`
  align-items: center;
  color: ${themeCssVariables.font.color.tertiary};
  display: flex;
  font-size: ${themeCssVariables.font.size.xs};
  font-weight: ${themeCssVariables.font.weight.medium};
  height: ${themeCssVariables.spacing[6]};
  padding: 0 ${themeCssVariables.spacing[1]};
`;

const StyledEmptyState = styled.div`
  color: ${themeCssVariables.font.color.tertiary};
  font-size: ${themeCssVariables.font.size.md};
  padding: ${themeCssVariables.spacing[6]} ${themeCssVariables.spacing[4]};
  text-align: center;
`;

export const CommandMenuContent = () => {
  const { t, i18n } = useLingui();
  const theme = useTheme();
  const listboxId = useId();
  const commands = useCommandMenuCommands();
  const { closeCommandMenu } = useCloseCommandMenu();
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedCommandIndex, setHighlightedCommandIndex] = useState(0);

  const filteredCommands = filterCommandMenuCommands(commands, searchQuery);
  // Keyboard order follows display order: groups first, then list order.
  const orderedCommands = COMMAND_MENU_GROUPS.flatMap((commandMenuGroup) =>
    filteredCommands.filter((command) => command.group === commandMenuGroup.id),
  );
  const highlightedCommand = orderedCommands[highlightedCommandIndex];
  const hasCommands = isNonEmptyArray(orderedCommands);

  const getOptionId = (command: CommandMenuCommand) =>
    `${listboxId}-${command.id}`;

  const highlightCommand = (commandIndex: number) => {
    setHighlightedCommandIndex(commandIndex);

    const command = orderedCommands[commandIndex];

    if (isDefined(command)) {
      document
        .getElementById(getOptionId(command))
        ?.scrollIntoView({ block: 'nearest' });
    }
  };

  const runCommand = (command: CommandMenuCommand) => {
    closeCommandMenu();
    command.onSelect();
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setHighlightedCommandIndex(0);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const commandCount = orderedCommands.length;
    const isToggleShortcut =
      (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';

    if (isToggleShortcut) {
      event.preventDefault();
      closeCommandMenu();
      return;
    }

    if (!hasCommands) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        highlightCommand((highlightedCommandIndex + 1) % commandCount);
        return;
      case 'ArrowUp':
        event.preventDefault();
        highlightCommand(
          (highlightedCommandIndex - 1 + commandCount) % commandCount,
        );
        return;
      case 'Enter':
        event.preventDefault();

        if (isDefined(highlightedCommand)) {
          runCommand(highlightedCommand);
        }
        return;
      default:
        return;
    }
  };

  return (
    <>
      <StyledSearchRow>
        <IconSearch
          size={theme.icon.size.md}
          stroke={theme.icon.stroke.sm}
          aria-hidden
        />
        <StyledSearchInput
          type="text"
          role="combobox"
          aria-label={t`Search commands`}
          aria-expanded={hasCommands}
          aria-controls={hasCommands ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            isDefined(highlightedCommand)
              ? getOptionId(highlightedCommand)
              : undefined
          }
          autoComplete="off"
          spellCheck={false}
          placeholder={t`Type anything...`}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
        />
      </StyledSearchRow>
      {!hasCommands && <StyledEmptyState>{t`No results`}</StyledEmptyState>}
      {hasCommands && (
        <StyledList id={listboxId} role="listbox" aria-label={t`Commands`}>
          {COMMAND_MENU_GROUPS.map((commandMenuGroup) => {
            const groupCommands = orderedCommands.filter(
              (command) => command.group === commandMenuGroup.id,
            );
            const groupLabelId = `${listboxId}-${commandMenuGroup.id}`;

            if (!isNonEmptyArray(groupCommands)) {
              return null;
            }

            return (
              <StyledGroup
                key={commandMenuGroup.id}
                role="group"
                aria-labelledby={groupLabelId}
              >
                <StyledGroupLabel id={groupLabelId}>
                  {i18n._(commandMenuGroup.label)}
                </StyledGroupLabel>
                {groupCommands.map((command) => {
                  const commandIndex = orderedCommands.indexOf(command);
                  const { Icon } = command;

                  return (
                    <ListItem
                      key={command.id}
                      id={getOptionId(command)}
                      render={<div role="option" />}
                      aria-selected={commandIndex === highlightedCommandIndex}
                      focused={commandIndex === highlightedCommandIndex}
                      selected={command.isCurrent ?? false}
                      indicator={command.isCurrent === true ? 'check' : 'none'}
                      description={command.description}
                      startIcon={
                        <Icon
                          size={theme.icon.size.md}
                          stroke={theme.icon.stroke.sm}
                        />
                      }
                      endIcon={
                        isDefined(command.hotkey) ? (
                          <HotkeyHint hotkey={command.hotkey} />
                        ) : undefined
                      }
                      onMouseMove={() =>
                        setHighlightedCommandIndex(commandIndex)
                      }
                      onClick={() => runCommand(command)}
                    >
                      {command.label}
                    </ListItem>
                  );
                })}
              </StyledGroup>
            );
          })}
        </StyledList>
      )}
    </>
  );
};
